import { formatBreweryMemberLabel } from '../brewery/formatBreweryMemberLabel';
import { resolveBreweryGroupDisplayName } from '../brewery/resolveBreweryGroupDisplayName';
import { encodeBrewhouseScopeValue } from './brewhouseActiveScope';

const GROUP_LABEL_PENDING = '…';
const GROUP_LABEL_FALLBACK = 'Brewery group';

function resolveGroupSelectorLabel(
  groupDisplayNamesById: Record<string, string>,
  groupId: string,
  groupDisplayNamesLoaded: boolean,
): string {
  const displayName = resolveBreweryGroupDisplayName(groupDisplayNamesById[groupId]);
  if (displayName) {
    return displayName;
  }
  if (!groupDisplayNamesLoaded) {
    return GROUP_LABEL_PENDING;
  }
  return GROUP_LABEL_FALLBACK;
}

export interface ManageableBreweryRow {
  breweryId: string;
  breweryName: string;
  breweryCity: string;
  breweryGroupId?: string | null;
}

export interface LocationSelectorOption {
  value: string;
  label: string;
  kind: 'group' | 'location';
  indent: number;
  breweryIds: string[];
}

function trimGroupId(groupId: string | null | undefined): string {
  return typeof groupId === 'string' ? groupId.trim() : '';
}

function sortBreweryRows(rows: ManageableBreweryRow[]): ManageableBreweryRow[] {
  const nameOf = (row: ManageableBreweryRow): string =>
    typeof row?.breweryName === 'string' ? row.breweryName : '';
  return [...rows].sort((left, right) =>
    nameOf(left).localeCompare(nameOf(right), undefined, {
      sensitivity: 'base',
    }),
  );
}

/**
 * Builds grouped location selector options for the Brewhouse header.
 * Groups with two or more manageable locations appear as a group row plus indented locations.
 * Single-member groups and ungrouped breweries appear as top-level location rows.
 */
export function buildBrewhouseLocationSelectorOptions(
  breweries: ManageableBreweryRow[],
  groupDisplayNamesById: Record<string, string>,
  groupDisplayNamesLoaded = true,
): LocationSelectorOption[] {
  if (!Array.isArray(breweries) || breweries.length === 0) {
    return [];
  }

  const groupMap = new Map<string, ManageableBreweryRow[]>();
  const standaloneRows: ManageableBreweryRow[] = [];

  breweries.forEach((row) => {
    const groupId = trimGroupId(row.breweryGroupId);
    if (groupId) {
      const members = groupMap.get(groupId) ?? [];
      members.push(row);
      groupMap.set(groupId, members);
      return;
    }
    standaloneRows.push(row);
  });

  const options: LocationSelectorOption[] = [];

  const sortedGroupIds = Array.from(groupMap.keys()).sort((left, right) => {
    const leftLabel = resolveGroupSelectorLabel(
      groupDisplayNamesById,
      left,
      groupDisplayNamesLoaded,
    );
    const rightLabel = resolveGroupSelectorLabel(
      groupDisplayNamesById,
      right,
      groupDisplayNamesLoaded,
    );
    return leftLabel.localeCompare(rightLabel, undefined, { sensitivity: 'base' });
  });

  sortedGroupIds.forEach((groupId) => {
    const members = sortBreweryRows(groupMap.get(groupId) ?? []);
    if (members.length >= 2) {
      const groupLabel = resolveGroupSelectorLabel(
        groupDisplayNamesById,
        groupId,
        groupDisplayNamesLoaded,
      );

      options.push({
        value: encodeBrewhouseScopeValue('group', groupId),
        label: groupLabel,
        kind: 'group',
        indent: 0,
        breweryIds: members.map((member) => member.breweryId),
      });

      members.forEach((member) => {
        options.push({
          value: encodeBrewhouseScopeValue('location', member.breweryId),
          label: formatBreweryMemberLabel({
            name: member.breweryName,
            city: member.breweryCity,
          }),
          kind: 'location',
          indent: 1,
          breweryIds: [member.breweryId],
        });
      });
      return;
    }

    standaloneRows.push(...members);
  });

  sortBreweryRows(standaloneRows).forEach((row) => {
    options.push({
      value: encodeBrewhouseScopeValue('location', row.breweryId),
      label: formatBreweryMemberLabel({
        name: row.breweryName,
        city: row.breweryCity,
      }),
      kind: 'location',
      indent: 0,
      breweryIds: [row.breweryId],
    });
  });

  return options;
}

export function shouldShowBrewhouseLocationSelector(
  options: LocationSelectorOption[],
): boolean {
  return options.length > 1;
}
